import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  NightsStay as NightIcon,
  WbSunny as DayIcon,
} from '@mui/icons-material';

const Turnos = () => {
  const theme = useTheme();
  const [selectedTurno, setSelectedTurno] = useState('');

  const turnosConfig = [
    { tipo: '7x7', descripcion: '7 días trabajo / 7 días franco', horario: '06:00 - 18:00', empleados: 85 },
    { tipo: '14x7', descripcion: '14 días trabajo / 7 días franco', horario: '06:00 - 18:00', empleados: 120 },
    { tipo: '21x7', descripcion: '21 días trabajo / 7 días franco', horario: '06:00 - 18:00', empleados: 45 },
    { tipo: 'Rotativo', descripcion: 'Diurno/Nocturno alternado', horario: 'Variable', empleados: 34 },
  ];

  const calendarioSemanal = [
    { empleado: 'Juan Rodríguez', turno: '14x7', lun: 'T', mar: 'T', mie: 'T', jue: 'T', vie: 'T', sab: 'T', dom: 'T' },
    { empleado: 'Pedro García', turno: '7x7', lun: 'T', mar: 'T', mie: 'T', jue: 'T', vie: 'T', sab: 'T', dom: 'T' },
    { empleado: 'Carlos Mendoza', turno: '14x7', lun: 'F', mar: 'F', mie: 'F', jue: 'F', vie: 'F', sab: 'F', dom: 'F' },
    { empleado: 'Ana Martínez', turno: '7x7', lun: 'T', mar: 'T', mie: 'T', jue: 'T', vie: 'T', sab: 'T', dom: 'T' },
    { empleado: 'Roberto Sánchez', turno: '21x7', lun: 'T', mar: 'T', mie: 'T', jue: 'T', vie: 'T', sab: 'T', dom: 'T' },
  ];

  const getDiaStyle = (valor) => {
    if (valor === 'T') return { bgcolor: alpha(theme.palette.success.main, 0.2), color: theme.palette.success.dark };
    if (valor === 'F') return { bgcolor: alpha(theme.palette.grey[400], 0.2), color: theme.palette.grey[600] };
    if (valor === 'N') return { bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.dark };
    return {};
  };

  const resumenStats = [
    { label: 'Personal en Turno', value: 189, icon: <PeopleIcon />, color: theme.palette.success.main },
    { label: 'En Franco', value: 76, icon: <CalendarIcon />, color: theme.palette.info.main },
    { label: 'Turno Diurno', value: 145, icon: <DayIcon />, color: theme.palette.warning.main },
    { label: 'Turno Nocturno', value: 44, icon: <NightIcon />, color: theme.palette.primary.main },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Gestión de Turnos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Turnos petroleros: 7x7, 14x7, 21x7 y rotativos
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<ScheduleIcon />}>
          Configurar Turnos
        </Button>
      </Box>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {resumenStats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: alpha(stat.color, 0.1), color: stat.color }}>
                    {stat.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color={stat.color}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tipos de Turnos */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Configuración de Turnos
          </Typography>
          <Grid container spacing={2}>
            {turnosConfig.map((turno, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h5" fontWeight={700} color="primary">
                    {turno.tipo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
                    {turno.descripcion}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Horario: {turno.horario}
                  </Typography>
                  <Chip 
                    label={`${turno.empleados} empleados`} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Calendario */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight={600}>
              Calendario Semanal - Febrero 2026
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box sx={{ width: 16, height: 16, borderRadius: 1, bgcolor: alpha(theme.palette.success.main, 0.3) }} />
                <Typography variant="caption">Trabajo (T)</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box sx={{ width: 16, height: 16, borderRadius: 1, bgcolor: alpha(theme.palette.grey[400], 0.3) }} />
                <Typography variant="caption">Franco (F)</Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Filtrar Turno</InputLabel>
                <Select value={selectedTurno} onChange={(e) => setSelectedTurno(e.target.value)} label="Filtrar Turno">
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="7x7">7x7</MenuItem>
                  <MenuItem value="14x7">14x7</MenuItem>
                  <MenuItem value="21x7">21x7</MenuItem>
                  <MenuItem value="Rotativo">Rotativo</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell>Empleado</TableCell>
                  <TableCell>Turno</TableCell>
                  <TableCell align="center">Lun 3</TableCell>
                  <TableCell align="center">Mar 4</TableCell>
                  <TableCell align="center">Mie 5</TableCell>
                  <TableCell align="center">Jue 6</TableCell>
                  <TableCell align="center">Vie 7</TableCell>
                  <TableCell align="center">Sab 8</TableCell>
                  <TableCell align="center">Dom 9</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {calendarioSemanal.map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: theme.palette.primary.main, fontSize: '0.7rem' }}>
                          {row.empleado.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Typography variant="body2">{row.empleado}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={row.turno} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    {['lun', 'mar', 'mie', 'jue', 'vie', 'sab', 'dom'].map((dia) => (
                      <TableCell key={dia} align="center">
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            mx: 'auto',
                            ...getDiaStyle(row[dia]),
                          }}
                        >
                          {row[dia]}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Turnos;
